import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../theme";

/**
 * AppModal - A standardized wrapper for all application modals/forms.
 *
 * @param {boolean} open - Controls visibility
 * @param {function} onClose - Function to close the modal
 * @param {string} title - The header text
 * @param {ReactNode} children - The form or content
 * @param {string} width - (Optional) 'xs' | 'sm' | 'md' | 'lg' | 'xl' (Default: 'sm')
 * @param {boolean} loading - (Optional) If true, prevents closing the modal via backdrop/escape
 * @param {ReactNode} actions - (Optional) Footer buttons (Submit/Cancel)
 * @param {boolean} showCloseIcon - (Optional) Show/Hide the 'X' button
 */
const AppModal = ({
  open,
  onClose,
  title,
  children,
  width = "sm",
  loading = false,
  actions,
  showCloseIcon = true,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Check if screen is small (mobile) to make modal full screen or adapt radius
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Handle close behavior (prevent closing if loading/submitting)
  const handleClose = (event, reason) => {
    if (loading || reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth // Always stretch to the maxWidth defined below
      maxWidth={width} // Controls the width ('sm', 'md', 'lg')
      fullScreen={isMobile} // Better UX on phones
      disableEscapeKeyDown
      PaperProps={{
        elevation: 24,
        sx: {
          // Use theme radius, unless full screen (mobile)
          borderRadius: isMobile ? 0 : theme.appRadius.lg,
          bgcolor: "background.paper",
          backgroundImage: "none", // Remove default MUI dark mode overlay
          overflow: "hidden", // Ensures header/footer corners respect radius
        },
      }}
    >
      {/* --- HEADER --- */}
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="h6"
          component="div"
          fontWeight="600"
          sx={{ color: "text.primary" }}
        >
          {title}
        </Typography>

        {showCloseIcon && (
          <IconButton
            aria-label="close"
            onClick={loading ? undefined : onClose}
            disabled={loading}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: theme.palette.error.main,
                bgcolor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {/* --- CONTENT --- */}
      {/* 'dividers' prop adds the line top/bottom if content scrolls */}
      <DialogContent
        dividers
        sx={{
          p: { xs: 2, md: 3 }, // Responsive padding
          bgcolor: "background.paper",
        }}
      >
        {children}
      </DialogContent>

      {/* --- FOOTER (ACTIONS) --- */}
      {/* Only render if 'actions' prop is passed */}
      {actions && (
        <DialogActions
          sx={{
            p: 2,
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.neutral.main, 0.05)
                : "background.paper",
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AppModal;
